/** A mailing address as the backend stores it (users and organizations alike). */
export interface MailingAddress {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
}

export const EMPTY_ADDRESS: MailingAddress = {
  line1: '', line2: '', city: '', state: '', zip: '',
};

// US states, DC and territories (same list as the mobile app's validator)
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY', 'DC', 'PR', 'GU', 'VI', 'AS', 'MP',
]);

/** Trimmed, inner spaces collapsed, state upper-cased. */
export function cleanAddress(address: MailingAddress): MailingAddress {
  const clean = (value: string) => (value || '').trim().replace(/\s+/g, ' ');
  return {
    line1: clean(address.line1),
    line2: clean(address.line2),
    city: clean(address.city),
    state: clean(address.state).toUpperCase(),
    zip: clean(address.zip),
  };
}

export type AddressProblems = Partial<Record<keyof MailingAddress, string>>;

/** Field → message for every field that can't be saved; empty when the address is valid. */
export function addressProblems(address: MailingAddress): AddressProblems {
  const value = cleanAddress(address);
  const problems: AddressProblems = {};
  if (!value.line1) problems.line1 = 'Please enter the street address.';
  if (!value.city) problems.city = 'Please enter the city.';
  if (!value.state) problems.state = 'Please enter the state.';
  else if (!US_STATE_CODES.has(value.state)) problems.state = 'Enter the 2-letter state code, e.g. NY.';
  if (!value.zip) problems.zip = 'Please enter the ZIP code.';
  else if (!/^\d{5}$/.test(value.zip)) problems.zip = 'Enter a 5-digit ZIP code.';
  return problems;
}

export function isSameAddress(a: MailingAddress, b: MailingAddress): boolean {
  const x = cleanAddress(a);
  const y = cleanAddress(b);
  return (Object.keys(x) as (keyof MailingAddress)[]).every((key) => x[key] === y[key]);
}

/** From the backend (`address_line_1` / `addressLine1` / `zipcode` / `zipCode`), nulls as ''. */
export function addressFromApi(value: any): MailingAddress {
  return {
    line1: value?.address_line_1 ?? value?.addressLine1 ?? '',
    line2: value?.address_line_2 ?? value?.addressLine2 ?? '',
    city: value?.city ?? '',
    state: value?.state ?? '',
    zip: value?.zipcode ?? value?.zipCode ?? '',
  };
}

/**
 * The backend input. Empty fields are sent as '' (not null): the organization update skips nulls,
 * so a removed second line would otherwise stay.
 */
export function addressToInput(address: MailingAddress) {
  const value = cleanAddress(address);
  return {
    address_line_1: value.line1,
    address_line_2: value.line2,
    city: value.city,
    state: value.state,
    zipcode: value.zip,
  };
}
